import React from 'react';
import { 
  Gavel, 
  Shield, 
  Description, 
  Payment, 
  Business, 
  Assignment,
  Policy,
  VerifiedUser,
  ContactSupport
} from '@mui/icons-material';

const TermsAndConditionsPageEN: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8" dir="ltr">
        
        {/* Header Section */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-dark rounded-2xl blur-lg opacity-30"></div>
              <div className="relative bg-gradient-to-r from-primary to-primary-dark p-5 rounded-2xl shadow-xl">
                <Gavel className="text-white text-4xl" />
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Terms and Conditions – Bazaar Marketplace
          </h1>
          
          <div className="h-1.5 w-32 bg-gradient-to-r from-primary to-primary-dark mx-auto rounded-full mb-4"></div>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Last Updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Important Notice */}
        <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-primary rounded-xl">
          <div className="flex items-start gap-4">
            <Shield className="text-primary text-2xl mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Important Notice</h3>
              <p className="text-gray-700 leading-relaxed">
                Welcome to Bazaar Marketplace. By using our platform (https://bazaar-marketplace.com), 
                you agree to the following terms and conditions. These terms govern your use of the platform 
                as a seller, buyer, or visitor. Please read them carefully.
              </p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {[
            {
              icon: <Description />,
              title: "1. Platform Nature",
              items: [
                "A platform for displaying products for companies and sellers.",
                "Facilitating buying and selling operations.",
                "Providing an organized, secure, and easy-to-use digital marketplace for Egyptian users."
              ],
              note: "Bazaar is not a direct seller or manufacturer. It is a technological tool for connecting users and facilitating e-commerce experiences."
            },
            {
              icon: <VerifiedUser />,
              title: "2. Seller Responsibilities",
              items: [
                "Full legal and governmental responsibility for their products and business.",
                "Ensure updating licenses, tax records, and legal documents.",
                "Submit required documents before displaying products.",
                "Respond to customer inquiries promptly and professionally."
              ],
              note: "Bazaar is not responsible for any violations or legal errors in seller documentation."
            },
            {
              icon: <Business />,
              title: "3. Buyer Responsibilities",
              items: [
                "Provide accurate information for delivery and communication.",
                "Review seller profiles and trust indicators before purchasing.",
                "Contact customer support for dispute resolution or returns."
              ]
            },
            {
              icon: <Shield />,
              title: "4. Product Verification and Trust",
              items: [
                "Ensure every listed product is genuine and available.",
                "Verify companies and sellers before displaying products.",
                "Track orders through trusted shipping companies.",
                "Provide instant support and return services."
              ]
            },
            {
              icon: <Description />,
              title: "5. Seller Dashboard and Analytics",
              items: [
                "Complete dashboard for managing products, orders, and customer communication.",
                "Real-time sales analytics.",
                "Tools for managing multiple branches."
              ]
            },
            {
              icon: <Policy />,
              title: "6. Modifications and Updates",
              items: [],
              note: "Bazaar reserves the right to modify terms and conditions at any time. Continued use of the platform means acceptance of any future changes, including integration with Facebook, WhatsApp, or others."
            },
            {
              icon: <Payment />,
              title: "7. Taxes",
              items: [
                "Seller is responsible for their tax obligations according to Egyptian law.",
                "Bazaar is registered with tax ID 752-382-462.",
                "We strictly prohibit tax evasion."
              ]
            },
            {
              icon: <Gavel />,
              title: "8. User Consent and Legal Recognition",
              items: [
                "All users acknowledge that these terms have been presented to them.",
                "No party may claim not to have read or understood them.",
                "Egyptian e-commerce laws apply to all operations."
              ]
            },
            {
              icon: <Shield />,
              title: "9. Liability Limitation",
              items: [
                "We are not responsible for delivery issues or seller products.",
                "We mediate disputes without legal representation.",
                "We protect your data only within our platform."
              ]
            },
            {
              icon: <ContactSupport />,
              title: "10. Contact and Support",
              items: [],
              note: (
                <>
                  <p className="mb-2">Email: <a href="mailto:support@bazaar-marketplace.com" className="text-primary font-medium hover:underline">support@bazaar-marketplace.com</a></p>
                  <p className="mb-2">Phone / WhatsApp: <a href="tel:+201503323006" className="text-primary font-medium hover:underline">+20 150 332 3006</a></p>
                  <p>Powered by: <strong className="text-primary">CODEOCEAN.TECH</strong></p>
                </>
              )
            }
          ].map((section, index) => (
            <section key={index} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-gradient-to-r from-primary/10 to-primary/20 p-3 rounded-lg">
                  <span className="text-primary text-xl">{section.icon}</span>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    {section.title}
                    <span className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent"></span>
                  </h2>
                  
                  {section.items.length > 0 && (
                    <ul className="space-y-2 mb-4">
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-3">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-gray-700 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.note && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border-l-4 border-primary">
                      {typeof section.note === 'string' ? (
                        <p className="text-gray-700 leading-relaxed">{section.note}</p>
                      ) : (
                        section.note
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary/10 to-primary/20 px-6 py-3 rounded-full">
              <span className="text-primary font-bold">Bazaar Marketplace</span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">Tax ID: 752-382-462</span>
            </div>
          </div>
          
          <div className="text-gray-500 text-sm leading-relaxed max-w-2xl mx-auto">
            <p className="mb-4">
              Thank you for joining Bazaar – The digital marketplace that reflects Egypt's economic ambition.
            </p>
            <p className="text-gray-400">
              All rights reserved © {new Date().getFullYear()} Bazaar Marketplace
            </p>
          </div>

          {/* Back to top */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="mt-6 px-6 py-2 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg hover:opacity-90 transition-opacity duration-300"
          >
            Back to Top
          </button>
        </div>

        {/* Legal Notice */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
          <p>
            This document is legally binding. Please keep a copy.
            <br />
            To ensure you're reading the latest version, visit: 
            <a href="https://bazaar-marketplace.com/terms-conditions" className="text-primary font-medium mx-1 hover:underline">
              https://bazaar-marketplace.com/terms-conditions
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPageEN;